import {useTaillightsController} from '@github-ui/taillights/provider'
import type {SourceRange, SourceSelection, SourceSymbol} from '@github-ui/taillights-worker/document'
import {startTransition, useCallback, useEffect, useRef, useState} from 'react'

const EMPTY_RANGES: readonly SourceRange[] = []

type SearchOptions = {
  getSelection: () => SourceSelection | null
  onSymbolSelected?: (symbol: SourceSymbol) => void
  onRenderError?: (error: Error) => void
  includeMatches?: boolean
  allowRangeSelection?: boolean
}

/** Inspects native selections, optionally searches for matching symbols, and ignores superseded worker responses. */
export function useSymbolSearch({
  getSelection,
  onSymbolSelected,
  onRenderError,
  includeMatches = false,
  allowRangeSelection = false,
}: SearchOptions) {
  const controller = useTaillightsController()
  const requestRef = useRef(0)
  const [result, setResult] = useState<{controller: typeof controller; ranges: readonly SourceRange[]}>()

  useEffect(
    () => () => {
      requestRef.current++
    },
    [controller],
  )

  const inspectSelection = useCallback(() => {
    const request = ++requestRef.current
    const selection = getSelection()
    if (!controller || !selection) return
    if (
      !allowRangeSelection &&
      (selection.start.line !== selection.end.line || selection.start.column !== selection.end.column)
    ) {
      return
    }
    void (async () => {
      try {
        const symbol = await controller.inspect({selection})
        if (!symbol || request !== requestRef.current) return
        onSymbolSelected?.(symbol)
        if (!includeMatches) return
        setResult(undefined)
        const search = await controller.search({query: symbol.text, kind: 'symbol', index: 0})
        if (request === requestRef.current) {
          startTransition(() => setResult({controller, ranges: search.highlights}))
        }
      } catch (error) {
        if (request !== requestRef.current) return
        const failure = error instanceof Error ? error : new Error(String(error))
        if (onRenderError) onRenderError(failure)
        else reportError(failure)
      }
    })()
  }, [controller, getSelection, includeMatches, allowRangeSelection, onSymbolSelected, onRenderError])

  return {inspectSelection, ranges: result?.controller === controller ? result.ranges : EMPTY_RANGES}
}
